import type { ComponentProps, ReactNode } from 'react';

import type { SiftConfig, StepData, StepType } from '../CGStep/CTSContentEditingPure';
import type { CadenceStepTypePure } from '../CGStep/types';
import type { TipTapDocument, TipTapVariable } from '../Input2/TipTapInput';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import type { TemplateBlockCardStep } from './templateStepDisplayInfo';
import { canAddAnyStepToBlock } from '../CampaignDayCard/blockStepRules';
import CTSContentEditingPure from '../CGStep/CTSContentEditingPure';
import { cn } from '../cn';
import { TemplateBlockAddButtonPure } from './TemplateBlockAddButtonPure';
import { TemplateBlockDelayEditPure } from './TemplateBlockDelayEditPure';
import { TemplateBlockHeaderPure } from './TemplateBlockHeaderPure';
import { TemplateStepPickerPure } from './TemplateStepPickerPure';
import { TemplateStepRowPure } from './TemplateStepRowPure';

/**
 * Step types that swap to the inline editor on click. Same set as the
 * campaign side (anything except `delay` / `wait_for_connect`).
 * Declared as a `const satisfies` tuple so TypeScript narrows
 * `step.type` to a `StepType` member without an `as` cast.
 */
const INLINE_EDITABLE_TYPES_LIST = [
  'visit',
  'connect',
  'message',
  'inmail',
  'email',
  'likeRecentPosts',
  'follow',
  'reminder',
  'sift',
  'call',
] as const satisfies ReadonlyArray<StepType>;

type InlineEditableType = (typeof INLINE_EDITABLE_TYPES_LIST)[number];

const INLINE_EDITABLE_TYPES: ReadonlySet<string> = new Set(INLINE_EDITABLE_TYPES_LIST);

function isInlineEditable(t: CadenceStepTypePure): t is InlineEditableType {
  return INLINE_EDITABLE_TYPES.has(t);
}

export type { TemplateBlockCardStep } from './templateStepDisplayInfo';

/**
 * Pure card representing a single delay block in a cadence template.
 * Mirrors `CampaignDayCardPure` in layout and chrome but drops
 * contact-only concerns:
 *
 *   - no per-contact `state` → no action-required (green) tint
 *   - no `templateHref` / `isDifferentFromTemplate` (this IS the template)
 *   - no "Today" detection
 *
 * Chrome (border / divider / step ordering) and the inline editor are
 * shared with the campaign card so the two pages feel like the same
 * product — only the data source differs.
 */
export type TemplateBlockCardPureProps = Omit<ComponentProps<'div'>, 'children'> & {
  /** Wait length for this block in whole days. */
  days: number;
  /** When true, the delay is calendar-week aligned (header pill + editor read in weeks). */
  alignWeek?: boolean;
  /** Block index within the template (0-based), forwarded to the header. */
  blockIndex?: number;
  /** Steps in the block, ordered. */
  steps: TemplateBlockCardStep[];

  /**
   * True for `wait_for_connect` blocks. The header label swaps from
   * "wait N days" to "Wait for connection" so users see this is a
   * branch-on-connection-accepted, not a fixed timer.
   */
  isWaitForConnect?: boolean;

  /** When true the header swaps the wait pill for the inline delay editor. */
  isEditingDelay: boolean | undefined;
  /** When true the footer swaps the add button for the step type picker. */
  isPickingStepType: boolean | undefined;
  /** When set, that step row swaps in the inline content editor. */
  editingStepId: string | undefined;
  /** When set, footer renders the inline ADD editor for a new step of this type. */
  addingStepType: CadenceStepTypePure | undefined;
  /** Per-type disabled reasons forwarded to the picker. */
  disabledStepReasons: Partial<Record<CadenceStepTypePure, string>> | undefined;
  /** Variables for `@` mentions in the inline editor. */
  variables: TipTapVariable[] | undefined;
  /** Documents for `/` mentions in the inline editor. */
  documents: TipTapDocument[] | undefined;
  /**
   * Subject of the email a step added via the inline ADD editor would continue, or null when
   * there's no earlier email step (hides the editor's Thread toggle). Derived by the host from the
   * template graph at the add anchor — see `CGStep/previousEmailStep`.
   */
  newStepPreviousEmailSubject?: string | null;
  /** Optional content rendered in the header right slot (e.g. delete block). */
  headerRightSlot?: ReactNode;

  // All callbacks are required so hosts can't accidentally drop a hook.
  onEditStep: (stepId: string) => void;
  onDeleteStep: (stepId: string) => void;
  onAddStep: () => void;
  onPickStepType: (type: CadenceStepTypePure) => void;
  onCancelPickStepType: () => void;
  onSaveStep: (stepId: string, data: StepData) => void | Promise<void>;
  onCancelEditStep: () => void;
  onSaveNewStep: (type: CadenceStepTypePure, data: StepData) => void | Promise<void>;
  onCancelAddStep: () => void;

  /** Header — swap in inline delay editor on click. */
  onEditDelay: () => void;
  onSaveDelay: (days: number, alignWeek: boolean) => void | Promise<void>;
  onCancelEditDelay: () => void;

  /** Render prop for the sift condition builder (mounted by the editor). */
  renderSiftConditionBuilder: (props: { value: SiftConfig; onChange: (value: SiftConfig) => void }) => ReactNode;

  /**
   * Reminder editor data — forwarded into the inline `CTSContentEditingPure`
   * editor when the user opens a `reminder` step. Without these, the
   * reminder dropdown renders empty (broken UX) and the user can't pick
   * or create reminder actions.
   */
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  /**
   * Whether the rep can enable per-step AI personalization. Gated by
   * the team's `campaign_research_enabled` flag at the host (the
   * personalization pipeline only runs in the candidate-staging flow,
   * which is the prefilter-on path). Forwarded into each inline
   * editor. Defaults to false at the host so we don't accidentally
   * surface the toggle outside the supported flow.
   */
  canPersonalize?: boolean;
  /** Admin-only display of which Claude model will run the personalize call. */
  personalizeModelInfo?: { modelId: string } | null;

  pickerOptionSize?: 'sm' | 'md';
  /** When true, the call step type is available (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
  /** Touch campaign lock: when set, the add-step picker offers ONLY this step type. */
  restrictToType?: CadenceStepTypePure | null;
  /**
   * Options for the "start campaign on complete" picker (touch campaigns only).
   * `value` is a PUBLIC template id, `label` the template name. The picker is
   * gated on `restrictToType != null`, so pass this whenever editing a touch
   * campaign's steps.
   */
  campaignTemplateOptions?: { value: string; label: string }[];
};

export function TemplateBlockCardPure({
  days,
  alignWeek,
  blockIndex,
  steps,
  isWaitForConnect,
  isEditingDelay,
  isPickingStepType,
  editingStepId,
  addingStepType,
  disabledStepReasons,
  variables,
  documents,
  newStepPreviousEmailSubject,
  headerRightSlot,
  onEditStep,
  onDeleteStep,
  onAddStep,
  onPickStepType,
  onCancelPickStepType,
  onSaveStep,
  onCancelEditStep,
  onSaveNewStep,
  onCancelAddStep,
  onEditDelay,
  onSaveDelay,
  onCancelEditDelay,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  canPersonalize = false,
  personalizeModelInfo = null,
  pickerOptionSize,
  voiceCallingEnabled,
  restrictToType,
  campaignTemplateOptions,
  className,
  ...divProps
}: TemplateBlockCardPureProps) {
  // Touch campaigns (single-type lock) allow only ONE step per day block.
  // `restrictToType` is the touch-campaign signal (non-null only for touch
  // templates), so once a block has a step we hide the add affordance
  // entirely (a permanently-disabled button that can never be used is worse
  // UX than no button).
  const touchCampaignBlockFull = restrictToType != null && steps.length >= 1;
  return (
    <div
      {...divProps}
      data-testid="template-block-card"
      data-block-index={blockIndex ?? ''}
      className={cn(
        'flex w-full flex-col overflow-hidden rounded-lg border shadow-xs',
        'border-lynch-200 bg-white dark:border-slate-800 dark:bg-slate-900',
        className,
      )}>
      <div
        className="border-b border-lynch-200 px-3 py-2 dark:border-slate-800"
        data-testid="template-block-header-wrapper">
        {!isEditingDelay && (
          <TemplateBlockHeaderPure
            days={days}
            alignWeek={alignWeek}
            blockIndex={blockIndex}
            // The wait pill in the header is suppressed by the
            // container — wait/edit/insert all happen on the connector
            // band rendered ABOVE this card, matching the contact
            // detail page's `CampaignDayDelayPure` chrome. Pass
            // `onEditDelay={undefined}` so the header renders just
            // "Day N" + (optional) wait_for_connect badge.
            onEditDelay={undefined}
            isWaitForConnect={isWaitForConnect}
            rightSlot={headerRightSlot}
          />
        )}
        {isEditingDelay && (
          <TemplateBlockDelayEditPure
            initialDays={days}
            initialAlignWeek={alignWeek}
            onSubmit={onSaveDelay}
            onCancel={onCancelEditDelay}
          />
        )}
      </div>
      <div className="flex flex-col divide-y divide-lynch-200 dark:divide-slate-800" data-testid="template-block-steps">
        {steps.length === 0 && (
          <div
            className="px-3 py-2 text-xs italic text-lynch-500 dark:text-lynch-400"
            data-testid="template-block-empty">
            No steps in this block
          </div>
        )}
        {steps.map((step) => {
          if (editingStepId === step.id && isInlineEditable(step.type)) {
            return (
              <div
                key={step.id}
                className="px-3 py-2"
                data-testid="template-step-editor"
                data-editing-step-id={step.id}
                data-editing-step-type={step.type}>
                <CTSContentEditingPure
                  stepType={step.type}
                  stepData={{
                    subject: step.subject,
                    body: step.body2,
                    postsToLike: step.postsToLike,
                    cc: step.cc,
                    bcc: step.bcc,
                    siftConditions: step.siftConditions as SiftConfig | null | undefined,
                    onlyOpenLink: step.onlyOpenLink,
                    autoWithdraw: step.autoWithdraw,
                    isPersonalized: step.isPersonalized,
                    personalizationPrompt: step.personalizationPrompt,
                    triggerCadenceTemplateIds: step.triggerCadenceTemplateIds,
                    replyToPrevious: step.replyToPrevious,
                  }}
                  previousEmailSubject={step.previousEmailSubject ?? null}
                  variables={variables}
                  documents={documents}
                  renderSiftConditionBuilder={renderSiftConditionBuilder}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder}
                  reminderCreating={reminderCreating}
                  onReminderError={onReminderError}
                  canPersonalize={canPersonalize}
                  personalizeModelInfo={personalizeModelInfo}
                  showTriggerCampaign={restrictToType != null}
                  campaignTemplateOptions={campaignTemplateOptions}
                  onSubmit={(data) => onSaveStep?.(step.id, data)}
                  onCancel={() => onCancelEditStep?.()}
                  onDelete={onDeleteStep != null ? () => onDeleteStep(step.id) : undefined}
                />
              </div>
            );
          }
          return <TemplateStepRowPure key={step.id} step={step} onEdit={onEditStep} />;
        })}
      </div>
      {addingStepType != null && isInlineEditable(addingStepType) && (
        <div
          className="border-t border-lynch-200 px-3 py-2 dark:border-slate-800"
          data-testid="template-step-add-editor"
          data-adding-step-type={addingStepType}>
          <CTSContentEditingPure
            stepType={addingStepType}
            stepData={{}}
            previousEmailSubject={newStepPreviousEmailSubject ?? null}
            variables={variables}
            documents={documents}
            renderSiftConditionBuilder={renderSiftConditionBuilder}
            reminderActions={reminderActions}
            reminderActionsLoading={reminderActionsLoading}
            onCreateReminder={onCreateReminder}
            reminderCreating={reminderCreating}
            onReminderError={onReminderError}
            canPersonalize={canPersonalize}
            personalizeModelInfo={personalizeModelInfo}
            submitLabel="Add"
            onSubmit={(data) => onSaveNewStep?.(addingStepType, data)}
            onCancel={() => onCancelAddStep?.()}
          />
        </div>
      )}
      {addingStepType == null && isPickingStepType && (
        <TemplateStepPickerPure
          existingTypes={steps.map((s) => s.type)}
          disabledTypeReasons={disabledStepReasons}
          optionSize={pickerOptionSize}
          voiceCallingEnabled={voiceCallingEnabled}
          restrictToType={restrictToType}
          onPickType={(type) => onPickStepType?.(type)}
          onCancel={onCancelPickStepType}
          className="border-t border-lynch-200 bg-lynch-50/50 dark:border-slate-800 dark:bg-slate-950/50"
        />
      )}
      {addingStepType == null && !isPickingStepType && !touchCampaignBlockFull && (
        <TemplateBlockAddButtonPure
          onClick={onAddStep}
          disabled={!canAddAnyStepToBlock(steps.map((s) => s.type))}
          disabledTooltip="No more step types can be added to this block"
        />
      )}
    </div>
  );
}
